
import type { LoanFlag } from '@ld.consumer-portal/models';
import { MOCK_LOANS } from './shared.mock';

export type LoanFlagName =
  | 'AutopayEnabled'
  | 'PaperlessEnabled'
  | 'PaymentsEnabled'
  | 'DocumentsEnabled'
  | 'MessagesEnabled'
  | 'PayoffQuoteEnabled'
  | 'HelocDrawEnabled'
  | 'ContactUpdateEnabled';

function createActiveFlag(loanNumber: string, name: string, id = 1): LoanFlag {
  const now = new Date();
  const startTime = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const endTime = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  return {
    id,
    loanNumber,
    name,
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString(),
  };
}

function buildLoanFlags(
  loanNumber: string,
  flags: Partial<Record<LoanFlagName, boolean>>
): Array<{ loanNumber: string; flagName: string; flagValue: boolean }> {
  const defaultFlags: Record<LoanFlagName, boolean> = {
    AutopayEnabled: true,
    PaperlessEnabled: true,
    PaymentsEnabled: true,
    DocumentsEnabled: true,
    MessagesEnabled: true,
    PayoffQuoteEnabled: true,
    HelocDrawEnabled: false,
    ContactUpdateEnabled: true,
  };

  const mergedFlags = { ...defaultFlags, ...flags };

  return Object.entries(mergedFlags).map(([flagName, flagValue]) => ({
    loanNumber,
    flagName,
    flagValue,
  }));
}

export const loanFlagsMock = {
  'all-features': [
    ...buildLoanFlags(MOCK_LOANS.mortgage.loanNumber, {
      AutopayEnabled: true,
      PaperlessEnabled: true,
      PaymentsEnabled: true,
      DocumentsEnabled: true,
      MessagesEnabled: true,
      PayoffQuoteEnabled: true,
      ContactUpdateEnabled: true,
    }),
    createActiveFlag(MOCK_LOANS.mortgage.loanNumber, 'FmSemiMonthlyAP', 100),
    createActiveFlag(MOCK_LOANS.mortgage.loanNumber, 'HelocEligibleLoans', 102),
  ],

  'limited-features': [
    ...buildLoanFlags(MOCK_LOANS.mortgage.loanNumber, {
      AutopayEnabled: false,
      PaperlessEnabled: true,
      PaymentsEnabled: true,
      DocumentsEnabled: true,
      MessagesEnabled: true,
      PayoffQuoteEnabled: false,
      ContactUpdateEnabled: true,
    }),
  ],

  'no-autopay': [
    ...buildLoanFlags(MOCK_LOANS.mortgage.loanNumber, {
      AutopayEnabled: false,
      PaperlessEnabled: true,
      PaymentsEnabled: true,
      DocumentsEnabled: true,
      MessagesEnabled: true,
      PayoffQuoteEnabled: true,
      ContactUpdateEnabled: true,
    }),
  ],

  'no-paperless': [
    ...buildLoanFlags(MOCK_LOANS.mortgage.loanNumber, {
      AutopayEnabled: true,
      PaperlessEnabled: false,
      PaymentsEnabled: true,
      DocumentsEnabled: true,
      MessagesEnabled: true,
      PayoffQuoteEnabled: true,
      ContactUpdateEnabled: true,
    }),
  ],

  'heloc-draw': [
    ...buildLoanFlags(MOCK_LOANS.heloc.loanNumber, {
      AutopayEnabled: true,
      PaperlessEnabled: true,
      PaymentsEnabled: true,
      DocumentsEnabled: true,
      MessagesEnabled: true,
      PayoffQuoteEnabled: false,
      HelocDrawEnabled: true,
      ContactUpdateEnabled: true,
    }),
  ],

  'multiple-loans': [
    ...buildLoanFlags(MOCK_LOANS.mortgage.loanNumber, {
      AutopayEnabled: true,
      PaperlessEnabled: true,
      PaymentsEnabled: true,
      DocumentsEnabled: true,
      MessagesEnabled: true,
      PayoffQuoteEnabled: true,
      ContactUpdateEnabled: true,
    }),
    createActiveFlag(MOCK_LOANS.mortgage.loanNumber, 'FmSemiMonthlyAP', 101),
    createActiveFlag(MOCK_LOANS.mortgage.loanNumber, 'HelocEligibleLoans', 103),
    ...buildLoanFlags(MOCK_LOANS.heloc.loanNumber, {
      AutopayEnabled: true,
      PaperlessEnabled: true,
      PaymentsEnabled: true,
      DocumentsEnabled: true,
      MessagesEnabled: true,
      PayoffQuoteEnabled: false,
      HelocDrawEnabled: true,
      ContactUpdateEnabled: true,
    }),
    ...buildLoanFlags(MOCK_LOANS.homeEquity.loanNumber, {
      AutopayEnabled: false,
      PaperlessEnabled: true,
      PaymentsEnabled: true,
      DocumentsEnabled: true,
      MessagesEnabled: true,
      PayoffQuoteEnabled: true,
      ContactUpdateEnabled: true,
    }),
  ],

  'payoff-ineligible': [
    ...buildLoanFlags(MOCK_LOANS.mortgage.loanNumber, {
      AutopayEnabled: true,
      PaperlessEnabled: true,
      PaymentsEnabled: true,
      DocumentsEnabled: true,
      MessagesEnabled: true,
      PayoffQuoteEnabled: true,
      ContactUpdateEnabled: true,
    }),
  ],

  'autopay-consent-required': [createActiveFlag(MOCK_LOANS.mortgage.loanNumber, 'Autopay', 1)],
};
